import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
} from "../../store/transactionSlice";
import TransactionCard from "./TransactionCard";
import FiltersBar from "./FiltersBar";
import TransactionFormDialog from "./TransactionFormDialog";

import AddIcon from "@mui/icons-material/Add";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import "./Transactions.css";

export default function Transactions() {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { items, total, page, limit, loading, error, summary } = useSelector(
    (s) => s.transactions,
  );

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    q: "",
    type: "",
    receiver: "",
  });
  const [openForm, setOpenForm] = useState(false);
  const [editTx, setEditTx] = useState(null);

  const params = useMemo(() => {
    const p = { page: 1, limit };
    if (filters.from) p.from = filters.from;
    if (filters.to) p.to = filters.to;
    if (filters.q) p.q = filters.q;
    if (filters.type) p.type = filters.type;
    if (filters.receiver) p.receiver = filters.receiver;
    return p;
  }, [filters, limit]);

  const load = (custom = {}) => {
    dispatch(
      fetchTransactions({ projectId, params: { ...params, ...custom } }),
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [projectId]);

  const totalPages = Math.ceil(total / limit);



  return (
    <div className="txPage">
      {/* HERO */}
      <div className="txHero">
        <div className="txHeroTop">
          <div style={{ minWidth: 0 }}>
            <div className="txTitle">Transactions</div>
            <div className="txSub">
              Project-wise expense records — clean, readable, professional.
            </div>

            <div className="txPills">
              <div className="txPill">
                <ReceiptLongOutlinedIcon style={{ fontSize: 18 }} />
                Total: {total || 0}
              </div>
              <div className="txPill">Showing: {items.length}</div>
            </div>
          </div>

          <div className="txHeroActions">
            <button
              className="txPrimaryBtn"
              onClick={() => {
                setEditTx(null);
                setOpenForm(true);
              }}
            >
              <AddIcon />
              Add Transaction
            </button>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        onApply={() => load({ page: 1 })}
        onReset={() => {
          setFilters({ from: "", to: "", q: "", type: "", receiver: "" });
          dispatch(
            fetchTransactions({ projectId, params: { page: 1, limit } }),
          );
        }}
      />

      {/* SUMMARY */}
      <div className="txSummary">
        <div className="txSummaryCard">
          <div className="txSummaryLabel">Total Quantity</div>
          <div className="txSummaryValue">
            {Number(summary?.qty || 0).toLocaleString()}
          </div>
          <div className="txSummaryHint">Qty from filtered records</div>
        </div>

        <div className="txSummaryCard">
          <div className="txSummaryLabel">Total Amount</div>
          <div className="txSummaryValue">
            {Number(summary?.totalAmount || 0).toLocaleString()}
          </div>
          <div className="txSummaryHint">Sum of all expenses</div>
        </div>

        <div className="txSummaryCard">
          <div className="txSummaryLabel">Paid Amount</div>
          <div className="txSummaryValue">
            {Number(summary?.totalPaid || 0).toLocaleString()}
          </div>
          <div className="txSummaryHint">Completed payments</div>
        </div>

        <div className="txSummaryCard txSummaryCard--warn">
          <div className="txSummaryLabel">Remaining</div>
          <div className="txSummaryValue">
            {Number(summary?.totalRemaining || 0).toLocaleString()}
          </div>
          <div className="txSummaryHint">Pending / unpaid</div>
        </div>
      </div>

      {/* STATES */}
      {loading && (
        <div className="pCenter">
          <div style={{ display: "grid", placeItems: "center", padding: 22 }}>
            Loading…
          </div>
        </div>
      )}

      {!loading && error && <div className="pError">{error}</div>}

      {/* LIST */}
      <div className="txGrid">
        {items.map((tx) => (
          <TransactionCard
            key={tx._id}
            tx={tx}
            onEdit={(t) => {
              setEditTx(t);
              setOpenForm(true);
            }}
            onDelete={(t) => {
              if (window.confirm(`Delete "${t.title}"?`)) {
                dispatch(deleteTransaction({ projectId, id: t._id }));
              }
            }}
          />
        ))}
      </div>

      {!loading && items.length === 0 && (
        <div className="pCenter" style={{ color: "#64748b", fontWeight: 900 }}>
          No transactions found. Add your first transaction.
        </div>
      )}

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="pagi">
          <button
            className="pagiBtn"
            disabled={page <= 1}
            onClick={() => load({ page: page - 1 })}
          >
            Prev
          </button>
          <div className="pagiText">
            Page {page} / {totalPages}
          </div>
          <button
            className="pagiBtn"
            disabled={page >= totalPages}
            onClick={() => load({ page: page + 1 })}
          >
            Next
          </button>
        </div>
      )}

      {/* FORM */}
      <TransactionFormDialog
      titles={items}
        open={openForm}
        onClose={() => setOpenForm(false)}
        initial={editTx}
        onSubmit={async (values) => {
          if (editTx?._id) {
            const res = await dispatch(
              updateTransaction({ projectId, id: editTx._id, body: values }),
            );
            if (!res.error) setOpenForm(false);
          } else {
            const res = await dispatch(
              createTransaction({ projectId, body: values }),
            );
            if (!res.error) setOpenForm(false);
          }
        }}
      />
    </div>
  );
}
